import React from "react"
import { Button, Modal } from "antd"

import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { HashChip } from "shared/ui/HashChip/HashChip.component"

interface CleanConfirmModalProps {
  open: boolean
  /** Questions the clean will act on — offered to cleaning and carrying off-path answers. */
  questionCount: number
  /** Off-path ANSWERS on those questions (respondents per question instance, summed). */
  offPathAnswers: number
  /** Data variables the report MEASURED as carrying them — a floor, not a ceiling. See the copy. */
  variableCount: number
  /** The flow the report walked, echoed by the engine. */
  variant: string
  /** The data stamp being approved. Never empty — the caller must not offer an apply without it. */
  dataHash: string
  /** The questionnaire stamp being approved: the routing the report walked. */
  qnrHash?: string
  onCancel: () => void
  onConfirm: () => void
}

const Line: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <li className="text-[12px] leading-snug text-slate-600">{children}</li>
)

/**
 * The consent step. This modal is the last thing between a report and a data file being
 * rewritten, so every sentence in it is a claim the user is entitled to have made explicitly
 * rather than discover afterwards:
 *
 *  - WHAT MOVES — the cleaned file replaces the project's working data. Not a copy, not a new
 *    project: the thing every later query and export reads.
 *  - WHAT IS KEPT — a copy of the data as it stands now is stored first, as a project document,
 *    and the raw import files were never touched by any of this. That is what makes the
 *    operation recoverable, and it is the reason a clean that cannot store the copy does not
 *    run at all.
 *  - WHAT IS NEVER TOUCHED — undetermined CELLS (a gating answer is blank or can't be read, so
 *    the routing about that cell is unknown; the guarantee is per cell, which is why it is not
 *    phrased per respondent — the same person can be undetermined on one cell and off path on
 *    another). Saying so here, at the moment of consent, is the difference between a safety
 *    property and a claim in a document nobody read.
 *  - WHAT IS BEING APPROVED — the data stamp and the questionnaire stamp. A clean is an approval
 *    of ONE report over ONE file and ONE routing; the server checks both hashes again before it
 *    writes and refuses if either moved.
 *
 * THE COUNTS ARE HONEST ABOUT THEIR UNITS, which is why this does not promise a number of
 * cells. Off-path answers are counted per respondent per question; CELLS are what gets blanked,
 * and one off-path answer on a five-option category question blanks five of them. The cell
 * figure is therefore reported after the clean, measured, rather than promised before it,
 * estimated. The variable figure is what the report MEASURED as carrying off-path answers — a
 * floor rather than a ceiling, since the clean counts data columns (a category question is
 * several) and variables whose off-path answers could not be counted carry no figure to add.
 * It is a description of the report, never a prediction of the clean.
 *
 * (Built on antd's `Modal` in the shape of `shared/ui/ConfimationModal` — same centred,
 * non-closable dialog with a Cancel/confirm button row — rather than wrapping it, because that
 * component's `message` is typed `string` and it renders no children, and widening it is
 * outside this task's file boundary. One prop change there, `message?: React.ReactNode`, would
 * let this become a wrapper.)
 *
 * @component
 */
const CleanConfirmModal: React.FC<CleanConfirmModalProps> = ({
  open,
  questionCount,
  offPathAnswers,
  variableCount,
  variant,
  dataHash,
  qnrHash,
  onCancel,
  onConfirm,
}) => (
  <Modal centered title="Apply this clean?" open={open} closable={false} footer={null}>
    <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 p-3">
      <div className="text-[13px] font-medium text-rose-800">
        {questionCount.toLocaleString()} question{questionCount === 1 ? "" : "s"} ·{" "}
        {offPathAnswers.toLocaleString()} off-path answer{offPathAnswers === 1 ? "" : "s"} ·{" "}
        {variableCount.toLocaleString()} variable{variableCount === 1 ? "" : "s"} measured as
        carrying them
      </div>
      <div className="mt-1 text-[11px] leading-snug text-rose-700">
        The answers those respondents gave to questions the routing never asked them will be
        blanked — some may still be refused, and the result says which. The number of CELLS
        blanked is reported when the clean finishes rather than promised now: one off-path answer
        on a five-option question blanks five cells, so the figures it reports are counted, not
        estimated here.
      </div>
    </div>

    <ul className="mb-3 ml-4 list-disc space-y-1.5">
      <Line>
        <strong>The cleaned file replaces this project&apos;s working data.</strong> Every query,
        table and export made from this project afterwards reads the cleaned file.
      </Line>
      <Line>
        <strong>A copy of the data as it is now is stored first</strong>, as a project document —
        its name is shown here when the clean finishes, and you will find it under Documents. If
        that copy cannot be stored, the clean does not run at all.
      </Line>
      <Line>
        <strong>Your raw import files are untouched.</strong> This rewrites the working data file
        only.
      </Line>
      <Line>
        {/* CELLS: the engine's guarantee is per cell, not per respondent — the same person can
            be undetermined on one cell and definitely off path on another. */}
        <strong>Undetermined cells are never touched.</strong> Where a gating answer is blank or
        can&apos;t be read, the routing about that cell is unknown — and unknown is not a
        finding, so those cells are left exactly as they are.
      </Line>
    </ul>

    <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-500">
      <span>
        You are approving the data{qnrHash ? " and the questionnaire" : ""} as{" "}
        {qnrHash ? "they are" : "it is"} stamped now
      </span>
      <HashChip label="data" hash={dataHash} length={12} />
      {qnrHash && <HashChip label="qnr" hash={qnrHash} length={12} />}
      <span>
        flow <span className="font-mono text-slate-700">{variant || "(none reported)"}</span>
      </span>
      <span className="basis-full leading-snug">
        {qnrHash
          ? "The server checks these stamps again before it writes. If the data or the questionnaire has moved in the meantime it refuses, and the data is not changed."
          : "The server checks this stamp again immediately before it writes. If the data has moved in the meantime it refuses, and nothing is changed."}
      </span>
    </div>

    <div className="flex flex-row space-x-2">
      {/* Cancel is outlined; the destructive confirm is solid red, as in
          every other confirmation. */}
      <DataQButton className="ml-auto" type="secondary" onClick={onCancel}>
        Cancel
      </DataQButton>
      <Button type="primary" danger onClick={onConfirm}>
        Clean the data
      </Button>
    </div>
  </Modal>
)

export default CleanConfirmModal
